import {
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    Output,
} from '@angular/core';
import { AbstractControl, FormGroup } from '@angular/forms';
import { Observable, of, OperatorFunction } from 'rxjs';
import {
    catchError,
    debounceTime,
    distinctUntilChanged,
    switchMap,
    tap,
} from 'rxjs/operators';

import { hasValidator } from '../_helpers/functions';

/**
 * Componente de autocomplete para busca via API.
 * A função apiSearch$ recebe o termo digitado e deve retornar um Observable com
 * a lista de resultados. O componente gerencia o estado de loading e erro.
 *
 * Por padrão, usa o campo 'name' dos objetos retornados como texto de exibição.
 * Configure itemValueName para usar outro campo.
 *
 * @example
 * <sume-autocomplete-search
 *   label="Produto"
 *   [formGroup]="formGroup"
 *   controlName="produto"
 *   [apiSearch$]="service.buscarProdutos"
 *   itemValueName="descricao"
 *   itemKeyName="id_produto"
 *   (current)="onSelectProduto($event)">
 * </sume-autocomplete-search>
 */

@Component({
    selector: 'sume-autocomplete-search',
    templateUrl: './autocomplete-search.component.html',
    styleUrls: ['./autocomplete-search.component.scss'],
})
export class AutocompleteSearchComponent implements AfterViewInit {
    @Input() label: string;
    @Input() controlName: string;
    @Input() formGroup: FormGroup;
    @Input() apiSearch$: (term: string) => Observable<any[]>;
    @Input() itemValueName = 'name';
    @Input() itemKeyName?: string;
    @Input() changeText = false;
    @Output() current = new EventEmitter<any>();

    searching = false;
    searchFailed = false;
    isRequired: boolean;

    constructor(private cdr: ChangeDetectorRef) {}

    ngAfterViewInit(): void {
        if (this.formGroup?.controls[this.controlName]?.validator) {
            this.isRequired = hasValidator(
                this.formGroup.controls[this.controlName],
                'required'
            );
        }
        this.cdr.detectChanges();
    }

    get formControl(): AbstractControl {
        return this.formGroup?.get(this.controlName);
    }

    search: OperatorFunction<string, readonly any[]> = (
        text$: Observable<string>
    ) =>
        text$.pipe(
            debounceTime(300),
            distinctUntilChanged(),
            tap(() => (this.searching = true)),
            switchMap(term =>
                this.apiSearch$(term).pipe(
                    tap(() => (this.searchFailed = false)),
                    catchError(() => {
                        this.searchFailed = true;
                        return of([]);
                    })
                )
            ),
            tap(() => (this.searching = false))
        );

    formatter = (item: any): string => item?.[this.itemValueName] ?? '';

    onSelect(event: any): void {
        const item = event.item;
        const value = this.itemKeyName ? item[this.itemKeyName] : item;
        this.current.emit(value);
    }
}
